import { useState } from 'react';
import { Loader2, Mail, Pencil, Plus, RefreshCw, Share2, ShieldAlert, Trash2, Users } from 'lucide-react';
import {
  startConnectGmail,
  useAccounts,
  useDisconnectAccount,
  useSyncAccount,
  useUpdateSenderName,
  type GmailAccount,
} from '@/lib/accounts';
import { toast } from 'sonner';
import { useMe } from '@/lib/auth';
import { ShareAccountDialog } from '@/components/ShareAccountDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SharedInboxDialog } from '@/components/SharedInboxDialog';
import { cn } from '@/lib/utils';
import { tagClasses } from '@/lib/tag-colors';

export function ConnectedAccounts() {
  const { data, isLoading, error } = useAccounts();
  const [sharedDialogOpen, setSharedDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-muted-foreground">Loading accounts…</CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/40">
        <CardContent className="p-6 text-destructive">
          Failed to load accounts: {error instanceof Error ? error.message : 'unknown'}
        </CardContent>
      </Card>
    );
  }

  const accounts = data?.accounts ?? [];
  const needsReconnect = accounts.filter((a) => !a.canBlockDomains);

  if (accounts.length === 0) {
    return (
      <>
      <SharedInboxDialog open={sharedDialogOpen} onOpenChange={setSharedDialogOpen} />
      <Card>
        <CardContent className="p-10 text-center">
          <Mail className="mx-auto size-8 text-muted-foreground" strokeWidth={1.5} />
          <h2 className="mt-4 text-lg font-semibold">No Gmail accounts yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Connect a Gmail account to start syncing threads.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button onClick={() => startConnectGmail('personal')}>
              <Mail strokeWidth={1.75} />
              Connect personal Gmail
            </Button>
            <Button variant="outline" onClick={() => setSharedDialogOpen(true)}>
              <Users strokeWidth={1.75} />
              Connect shared inbox
            </Button>
          </div>
        </CardContent>
      </Card>
      </>
    );
  }

  return (
    <>
    <SharedInboxDialog open={sharedDialogOpen} onOpenChange={setSharedDialogOpen} />
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Connected accounts ({accounts.length})
        </CardTitle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Plus className="size-3.5" strokeWidth={2} />
              Add
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => startConnectGmail('personal')}>
              <Mail className="size-4" strokeWidth={1.75} />
              Personal — visible only to you
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                // Defer so the dropdown's own focus-restore doesn't fight the
                // dialog's autofocus.
                e.preventDefault();
                setTimeout(() => setSharedDialogOpen(true), 0);
              }}
            >
              <Users className="size-4" strokeWidth={1.75} />
              Shared — visible to the team
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="pt-0">
        {needsReconnect.length > 0 && <ReconnectBanner accounts={needsReconnect} />}
        <ul>
          {accounts.map((a, i) => (
            <li key={a.id}>
              {i > 0 && <Separator />}
              <AccountRow a={a} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
    </>
  );
}

/**
 * Shown when one or more inboxes were connected before the
 * gmail.settings.basic scope existed. They can report spam fine, but can't
 * create the Gmail filters that power "Block domain" until reconnected.
 */
function ReconnectBanner({ accounts }: { accounts: GmailAccount[] }) {
  const one = accounts.length === 1;
  return (
    <div className="mb-4 rounded-lg border border-amber-300/70 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-950/30">
      <div className="flex gap-2.5">
        <ShieldAlert
          className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500"
          strokeWidth={2}
        />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Domain blocking needs an extra Gmail permission
          </p>
          <p className="mt-0.5 text-amber-800/80 dark:text-amber-300/70">
            {one ? 'This inbox was' : `${accounts.length} inboxes were`} connected before Wharf
            could create Gmail filters. Reconnect to enable “Block domain” — no mail, history, or
            settings are lost. (Reporting spam already works.)
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {accounts.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2">
                <span className="truncate text-sm text-amber-900 dark:text-amber-200">
                  {a.emailAddress}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 gap-1.5 border-amber-300 bg-white/60 text-amber-900 hover:bg-white dark:border-amber-500/40 dark:bg-transparent dark:text-amber-200"
                  onClick={() => startConnectGmail(a.kind === 'shared' ? 'shared' : 'personal')}
                >
                  <RefreshCw className="size-3.5" strokeWidth={2} />
                  Reconnect
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function AccountRow({ a }: { a: GmailAccount }) {
  const disconnect = useDisconnectAccount();
  const sync = useSyncAccount();
  const me = useMe();
  const [renameOpen, setRenameOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const Icon = a.kind === 'shared' ? Users : Mail;
  const busy = a.syncStatus === 'initial_backfill' || sync.isPending;
  // Owner-only buttons: rename, share, disconnect.
  const isOwner = me.data?.user.id === a.ownerUserId;

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <Icon className="size-5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{a.emailAddress}</span>
            {a.kind === 'shared' && (
              <Badge variant="secondary" className="h-4 px-1.5 text-[9px]">
                shared
              </Badge>
            )}
            {a.nickname && (
              <span
                className={cn(
                  'inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium ring-1 ring-inset',
                  tagClasses(a.nicknameColor ?? 'sky'),
                )}
              >
                {a.nickname}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <StatusBadge status={a.syncStatus} />
            {a.lastSyncedAt && (
              <span title={new Date(a.lastSyncedAt).toLocaleString()}>
                synced {formatRelative(a.lastSyncedAt)}
              </span>
            )}
            {a.syncError && <span className="text-destructive">{a.syncError}</span>}
          </div>
          <SenderNameRow account={a} editable={isOwner} />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isOwner && a.kind === 'shared' && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setRenameOpen(true)}
                >
                  <Pencil className="size-4" strokeWidth={1.75} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {a.nickname ? 'Rename inbox' : 'Add nickname'}
              </TooltipContent>
            </Tooltip>
            <SharedInboxDialog
              open={renameOpen}
              onOpenChange={setRenameOpen}
              account={a}
            />
          </>
        )}
        {/* Share — visible to everyone (read-only for non-owners), but the
            dialog itself hides the add/remove controls when canManage is false. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => setShareOpen(true)}
            >
              <Share2 className="size-4" strokeWidth={1.75} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isOwner ? 'Manage access' : 'Who has access'}</TooltipContent>
        </Tooltip>
        <ShareAccountDialog account={a} open={shareOpen} onOpenChange={setShareOpen} />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => sync.mutate(a.id)}
              disabled={busy}
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <RefreshCw className="size-4" strokeWidth={1.75} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sync now</TooltipContent>
        </Tooltip>

        {isOwner && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => {
                  if (
                    confirm(
                      `Disconnect ${a.emailAddress}? This removes Wharf Mail's access; you can reconnect any time.`,
                    )
                  ) {
                    disconnect.mutate(a.id);
                  }
                }}
                disabled={disconnect.isPending}
              >
                {disconnect.isPending ? (
                  <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Trash2 className="size-4" strokeWidth={1.75} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Disconnect</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: GmailAccount['syncStatus'] }) {
  const { label, variant } = describeStatus(status);
  return (
    <Badge variant={variant} className="h-4 px-1.5 text-[9px] font-medium uppercase tracking-wide">
      {label}
    </Badge>
  );
}

function describeStatus(
  status: GmailAccount['syncStatus'],
): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } {
  switch (status) {
    case 'initial_backfill':
      return { label: 'backfilling', variant: 'default' };
    case 'incremental':
      return { label: 'synced', variant: 'secondary' };
    case 'paused':
      return { label: 'paused', variant: 'outline' };
    case 'error':
      return { label: 'error', variant: 'destructive' };
    case 'idle':
    default:
      return { label: 'idle', variant: 'outline' };
  }
}

/** "Sends as: Tech Generation" with inline owner-only editing. This is the
 *  name recipients see — Wharf used to send NO name (bare address). */
function SenderNameRow({ account, editable }: { account: GmailAccount; editable: boolean }) {
  const update = useUpdateSenderName();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(account.senderName ?? '');

  function save() {
    update.mutate(
      { id: account.id, senderName: value.trim() || null },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success(
            value.trim()
              ? `Outgoing mail will show "${value.trim()} <${account.emailAddress}>"`
              : 'Sender name cleared — outgoing mail shows the bare address',
          );
        },
        onError: () => toast.error('Only the inbox owner can change the sender name'),
      },
    );
  }

  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-1.5">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          maxLength={100}
          placeholder="e.g. Tech Generation"
          className="h-7 w-56 rounded-md border bg-transparent px-2 text-xs outline-none focus:ring-2 focus:ring-ring"
        />
        <Button size="xs" onClick={save} disabled={update.isPending}>
          Save
        </Button>
        <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-0.5 text-xs text-muted-foreground">
      Sends as:{' '}
      {account.senderName ? (
        <span className="text-foreground/80">"{account.senderName}"</span>
      ) : (
        <span className="text-amber-600 dark:text-amber-400">no name — recipients see the bare address</span>
      )}
      {editable && (
        <button
          type="button"
          onClick={() => {
            setValue(account.senderName ?? '');
            setEditing(true);
          }}
          className="ml-1.5 underline decoration-dotted hover:text-foreground"
        >
          edit
        </button>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
